import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../../interceptors";

/* eslint-disable react-hooks/exhaustive-deps */

const ROUND_TYPES = [
  { value: "", label: "(unset)" },
  { value: "SCREENING", label: "Screening", color: "#7B5AC4" },
  { value: "TECHNICAL", label: "Technical", color: "#10B981" },
  { value: "BEHAVIORAL", label: "Behavioral", color: "#2563EB" },
  { value: "MANAGER", label: "Manager", color: "#F59E0B" },
  { value: "BAR_RAISER", label: "Bar raiser", color: "#EC4899" },
];

const typeColor = (t) => ROUND_TYPES.find((x) => x.value === t)?.color || "#64748B";

export default function RoundBuilder() {
  const { jobId: jobIdParam } = useParams();
  const jobId = jobIdParam ? Number(jobIdParam) : null;
  const nav = useNavigate();

  const [rounds, setRounds] = useState([]);
  const [weightsSum, setWeightsSum] = useState(0);
  const [weightsValid, setWeightsValid] = useState(false);
  const [rubrics, setRubrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(null); // null | "new" | round
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    document.title = "Interview rounds - Ample Hire";
  }, []);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      return;
    }
    load();
  }, [jobId]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [roundsRes, rubricsRes] = await Promise.all([
        axiosInstance.post("/job-posting/define-interviews/get-all", { jobId: String(jobId) }),
        axiosInstance.get("/job-posting/scorecards/templates"),
      ]);
      setRounds(Array.isArray(roundsRes.data?.interviewRounds) ? roundsRes.data.interviewRounds : []);
      setWeightsSum(Number(roundsRes.data?.weightsSum) || 0);
      setWeightsValid(!!roundsRes.data?.weightsSumValid);
      setRubrics(Array.isArray(rubricsRes.data) ? rubricsRes.data : []);
    } catch (e) {
      setError(e?.message || "Failed to load rounds");
    } finally {
      setLoading(false);
    }
  };

  const rubricById = useMemo(() => {
    const m = new Map();
    rubrics.forEach((r) => m.set(Number(r.id), r));
    return m;
  }, [rubrics]);

  const onSave = async (form) => {
    setSaveError(null);
    try {
      const body = {
        jobId,
        interviewRound: form.interviewRound,
        roundType: form.roundType || null,
        weightPct: form.weightPct != null ? Number(form.weightPct) : null,
        durationMin: form.durationMin != null ? Number(form.durationMin) : 30,
        description: form.description || null,
        rubricId: form.rubricId ? Number(form.rubricId) : null,
        // Preserve existing fields that this UI doesn't manage by re-sending what we read.
        defaultLanguage: editing && editing !== "new" ? editing.defaultLanguage : "en",
        interviewTeam: editing && editing !== "new" ? editing.interviewTeam || [] : [],
        domainSkills: editing && editing !== "new" ? editing.domainSkills || [] : [],
        softSkills: editing && editing !== "new" ? editing.softSkills || [] : [],
        interviewTranslationLanguages:
          editing && editing !== "new" ? editing.interviewTranslationLanguages || [] : [],
        isRoundPublished: editing && editing !== "new" ? !!editing.isRoundPublished : false,
        cameraOn: editing && editing !== "new" ? editing.cameraOn !== false : true,
        randomiseCompetancySections:
          editing && editing !== "new" ? !!editing.randomiseCompetancySections : false,
        randomiseCompetancyQuestions:
          editing && editing !== "new" ? !!editing.randomiseCompetancyQuestions : false,
        randomiseMcqOptions: editing && editing !== "new" ? !!editing.randomiseMcqOptions : false,
        // Feature #4.5 — per-skill weights JSON. Caller-side validation in editor;
        // backend re-validates each list sums to 100.
        skillWeights: form.skillWeights ? JSON.stringify(form.skillWeights) : null,
      };
      const r = await axiosInstance.post("/job-posting/define-interviews/save", body);
      if (r?.data?.status === false) {
        setSaveError(r.data.message || "Save failed");
        return;
      }
      setEditing(null);
      await load();
    } catch (e) {
      setSaveError(e?.data?.message || e?.message || "Save failed");
    }
  };

  if (!jobId) {
    return (
      <div className="page-enter" style={{ maxWidth: 900, margin: "0 auto", padding: "24px 32px" }}>
        <h1 className="text-2xl font-semibold text-vf-base">Interview rounds</h1>
        <p className="text-sm text-vf-muted mt-1 mb-5">
          Open the rounds builder via <code className="bg-slate-100 px-1 rounded">/admin/jobs/&lt;jobId&gt;/rounds</code>.
        </p>
        <button onClick={() => nav("/admin/jobs")} className="px-3 py-1.5 rounded bg-vf-primary text-white text-sm">
          Go to jobs
        </button>
      </div>
    );
  }

  return (
    <div className="page-enter" style={{ maxWidth: 1000, margin: "0 auto", padding: "24px 32px" }}>
      <button onClick={() => nav("/admin/jobs")} className="text-xs text-vf-primary hover:underline mb-2">
        ← Back to jobs
      </button>
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Interview rounds</h1>
          <p className="text-sm text-vf-muted mt-1">Job #{jobId} · compose the rounds and their contribution to the overall score.</p>
        </div>
        <div className="flex items-center gap-3">
          <WeightsIndicator sum={weightsSum} valid={weightsValid} />
          <button
            onClick={() => setEditing("new")}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-sm font-medium"
          >
            + Add round
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl py-10 text-center text-sm text-vf-muted">
          Loading…
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl py-6 text-center">
          <div className="text-sm text-red-700 mb-2">{error}</div>
          <button onClick={load} className="px-3 py-1 rounded border border-red-300 text-xs">Retry</button>
        </div>
      ) : rounds.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-10 text-center text-sm text-vf-muted">
          No rounds defined for this job yet. Click "+ Add round" to create the first.
        </div>
      ) : (
        <div className="space-y-2">
          {rounds.map((r, i) => (
            <RoundCard
              key={`${r.jobId}-${r.interviewRound}`}
              round={r}
              index={i + 1}
              rubricName={rubricById.get(Number(r.rubricId))?.name}
              onEdit={() => setEditing(r)}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing && (
          <RoundEditor
            initial={editing === "new" ? null : editing}
            rubrics={rubrics}
            existingNames={rounds.map((r) => r.interviewRound)}
            saveError={saveError}
            onCancel={() => { setEditing(null); setSaveError(null); }}
            onSave={onSave}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function WeightsIndicator({ sum, valid }) {
  const tone = valid
    ? { fg: "#065F46", bg: "#D1FAE5", border: "#A7F3D0" }
    : { fg: "#92400E", bg: "#FEF3C7", border: "#FDE68A" };
  return (
    <div
      title="Sum of weight_pct across all rounds for this job"
      className="text-xs font-semibold px-3 py-1 rounded-full border"
      style={{ background: tone.bg, color: tone.fg, borderColor: tone.border }}
    >
      Weights {Number(sum).toFixed(0)} / 100
    </div>
  );
}

function RoundCard({ round, index, rubricName, onEdit }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-4">
      <div className="w-8 h-8 rounded-full bg-vf-primary/10 text-vf-primary text-sm font-bold flex items-center justify-center flex-shrink-0">
        {index}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-vf-base">{round.interviewRound}</div>
          {round.roundType && (
            <span
              className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold"
              style={{ background: typeColor(round.roundType) + "22", color: typeColor(round.roundType) }}
            >
              {round.roundType.replace("_", " ")}
            </span>
          )}
          {round.weightPct != null && (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-vf-muted bg-slate-100 px-2 py-0.5 rounded">
              {Number(round.weightPct).toFixed(0)}% weight
            </span>
          )}
          {round.durationMin != null && (
            <span className="text-[10px] text-vf-muted">{round.durationMin} min</span>
          )}
        </div>
        {rubricName && (
          <div className="text-[11px] text-vf-muted mt-0.5">
            Rubric: <span className="font-medium text-vf-primary">{rubricName}</span>
          </div>
        )}
        {round.description && (
          <div className="text-xs text-vf-muted mt-1 leading-relaxed">{round.description}</div>
        )}
      </div>
      <button onClick={onEdit} className="px-2.5 py-1 rounded border border-slate-300 text-xs font-medium">
        Edit
      </button>
    </div>
  );
}

function parseSkillWeights(raw) {
  if (!raw) return { domain: [], soft: [] };
  if (typeof raw === "object") {
    return { domain: raw.domain || [], soft: raw.soft || [] };
  }
  try {
    const obj = JSON.parse(raw);
    return { domain: obj?.domain || [], soft: obj?.soft || [] };
  } catch {
    return { domain: [], soft: [] };
  }
}

function RoundEditor({ initial, rubrics, existingNames, saveError, onCancel, onSave }) {
  const isNew = !initial;
  const [interviewRound, setInterviewRound] = useState(initial?.interviewRound || "");
  const [roundType, setRoundType] = useState(initial?.roundType || "");
  const [weightPct, setWeightPct] = useState(initial?.weightPct ?? "");
  const [durationMin, setDurationMin] = useState(initial?.durationMin ?? 30);
  const [description, setDescription] = useState(initial?.description || "");
  const [rubricId, setRubricId] = useState(initial?.rubricId || "");

  // Feature #4.5 — per-skill weights.
  const initialSW = useMemo(() => parseSkillWeights(initial?.skillWeights), [initial]);
  const [domainSkills, setDomainSkills] = useState(initialSW.domain);
  const [softSkills, setSoftSkills] = useState(initialSW.soft);

  const domainSum = domainSkills.reduce((s, x) => s + (Number(x.weight) || 0), 0);
  const softSum = softSkills.reduce((s, x) => s + (Number(x.weight) || 0), 0);
  const domainValid = domainSkills.length === 0 || Math.abs(domainSum - 100) < 0.01;
  const softValid = softSkills.length === 0 || Math.abs(softSum - 100) < 0.01;

  const nameValid =
    interviewRound.trim().length > 0 &&
    (!isNew || !existingNames.includes(interviewRound.trim()));
  const weightValid = weightPct === "" || (Number(weightPct) >= 0 && Number(weightPct) <= 100);
  const canSave = nameValid && weightValid && domainValid && softValid;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onCancel}
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", zIndex: 90 }}
      />
      <motion.aside
        initial={{ x: 480 }} animate={{ x: 0 }} exit={{ x: 480 }}
        transition={{ type: "tween", duration: 0.22 }}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: 480,
          background: "white", zIndex: 100, display: "flex", flexDirection: "column",
          boxShadow: "-12px 0 32px rgba(15,23,42,0.12)"
        }}
      >
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-vf-base">{isNew ? "New round" : "Edit round"}</div>
            <div className="text-[11px] text-vf-muted mt-0.5">
              {isNew ? "Round name is permanent — it's used as a key." : "Round name is the row key and can't be edited."}
            </div>
          </div>
          <button onClick={onCancel} className="w-6 h-6 text-slate-500 hover:bg-slate-100 rounded" aria-label="Close">×</button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <Field label="Name (permanent)">
            <input
              value={interviewRound}
              onChange={(e) => setInterviewRound(e.target.value)}
              placeholder="e.g. Recruiter screen"
              disabled={!isNew}
              className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm disabled:bg-slate-50 disabled:text-slate-500"
            />
            {!nameValid && isNew && (
              <div className="text-[11px] text-red-600 mt-1">
                {interviewRound.trim() === "" ? "Required." : "Already used for this job."}
              </div>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select
                value={roundType}
                onChange={(e) => setRoundType(e.target.value)}
                className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm bg-white"
              >
                {ROUND_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Duration (min)">
              <input
                type="number"
                min="1"
                max="240"
                value={durationMin}
                onChange={(e) => setDurationMin(Number(e.target.value) || 30)}
                className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Weight % (contribution to overall)">
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={weightPct}
                onChange={(e) => setWeightPct(e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="0–100"
                className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm"
              />
              {!weightValid && (
                <div className="text-[11px] text-red-600 mt-1">Must be 0–100.</div>
              )}
            </Field>
            <Field label="Rubric">
              <select
                value={rubricId || ""}
                onChange={(e) => setRubricId(e.target.value)}
                className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm bg-white"
              >
                <option value="">— none —</option>
                {rubrics.map((r) => (
                  <option key={r.id} value={r.id}>{r.name || `Rubric #${r.id}`}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="What is this round about? Optional."
              className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm"
            />
          </Field>

          <SkillWeightSection
            title="Domain skills"
            hint="Per-skill weights for the domain side. Leave empty to skip; otherwise weights must sum to 100."
            rows={domainSkills}
            setRows={setDomainSkills}
            sum={domainSum}
            valid={domainValid}
          />
          <SkillWeightSection
            title="Soft skills"
            hint="Same rule: empty or sum to 100."
            rows={softSkills}
            setRows={setSoftSkills}
            sum={softSum}
            valid={softValid}
          />

          {saveError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded">{saveError}</div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button onClick={onCancel} className="px-3 py-1.5 rounded border border-slate-300 text-xs">Cancel</button>
          <button
            onClick={() =>
              onSave({
                interviewRound: interviewRound.trim(),
                roundType: roundType || null,
                weightPct: weightPct === "" ? null : Number(weightPct),
                durationMin: Number(durationMin) || 30,
                description: description || null,
                rubricId: rubricId || null,
                // Only emit skillWeights if at least one side has rows;
                // otherwise leave null so save doesn't overwrite existing data.
                skillWeights:
                  domainSkills.length > 0 || softSkills.length > 0
                    ? { domain: domainSkills, soft: softSkills }
                    : null,
              })
            }
            disabled={!canSave}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save round
          </button>
        </footer>
      </motion.aside>
    </>
  );
}

function SkillWeightSection({ title, hint, rows, setRows, sum, valid }) {
  const update = (i, p) => setRows(rows.map((r, j) => (i === j ? { ...r, ...p } : r)));
  const remove = (i) => setRows(rows.filter((_, j) => j !== i));
  const add = () => setRows([...rows, { name: "", weight: 0 }]);
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-vf-muted font-semibold">{title}</div>
          <div className="text-[10px] text-vf-muted">{hint}</div>
        </div>
        {rows.length > 0 && (
          <div className={`text-xs font-semibold ${valid ? "text-emerald-700" : "text-red-700"}`}>
            Sum: {sum.toFixed(0)} / 100
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <button
          onClick={add}
          className="w-full px-2 py-1.5 border border-dashed border-slate-300 rounded text-xs text-vf-muted hover:text-vf-base hover:border-slate-400"
        >
          + Add {title.toLowerCase()}
        </button>
      ) : (
        <div className="space-y-1">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                value={r.name || ""}
                onChange={(e) => update(i, { name: e.target.value })}
                placeholder="Skill name"
                className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={r.weight ?? 0}
                  onChange={(e) => update(i, { weight: Number(e.target.value) || 0 })}
                  className="w-16 px-2 py-1 border border-slate-300 rounded text-xs text-right tabular-nums"
                />
                <span className="text-[10px] text-vf-muted">%</span>
              </div>
              <button onClick={() => remove(i)} className="text-xs text-red-700 px-2">
                ×
              </button>
            </div>
          ))}
          <button onClick={add} className="text-xs text-vf-primary font-medium">
            + Add row
          </button>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-vf-muted font-semibold mb-1">{label}</div>
    {children}
  </div>
);
